/**
 * Self-contained design tokens for this page only.
 *
 * These values match Markit's real design system (colors from the Figma
 * Design Library page, node 40344:18750; typography per the "SF Pro Text
 * only" rule in the product-design skill) — but are kept local here rather
 * than importing `Colors` from `src/utils/colors.ts` or the global
 * `GlobalTextStyles.css` classes, so this folder has zero import-level tie
 * into the shared design-system files. See this folder's README for why.
 *
 * Light mode only for v1 — desktop web defaults to light mode per Markit's
 * own design conventions, and this is a simple internal tool, not a full
 * product surface that needs a dark-mode pass.
 */

export const colors = {
  bg: "#FAFAFA",
  surface: "#FFFFFF",
  surfaceMuted: "#F4F5F6",
  ink: "#1C1C1D",
  inkMuted: "#666666",
  inkFaint: "#929292",
  border: "#EDEDED",
  borderStrong: "#B9B9B9",

  // primary accent — Markit's real primary action color, sampled directly
  // from a live button component (Components page), not blue
  accent: "#000000",

  angelAccent: "#313135",
  fundAccent: "#000000",

  looking: "#F92D48",
  lookingBg: "#FEE0E4",
  have: "#EF933D",
  haveBg: "#FDEFE2",
  covered: "#1EA05C",
  coveredBg: "#E2FAED",
} as const;

// SF Pro Text is Apple's system font and isn't licensed for embedding via
// Google Fonts or any other web font host — the accurate way to render it is
// the native system-font stack, which resolves to real San Francisco/SF Pro
// on Mac and iOS.
export const fontFamily =
  '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

// Matches the product-design skill's real type scale (SF Pro Text only,
// named Figma text styles) — the subset this page actually needs.
export const type = {
  largeHeading: { fontFamily, fontSize: 32, fontWeight: 700 as const },
  sectionTitle: { fontFamily, fontSize: 18, fontWeight: 600 as const },
  bodyBold: { fontFamily, fontSize: 14, fontWeight: 600 as const },
  bodyMedium: { fontFamily, fontSize: 14, fontWeight: 500 as const },
  bodyRegular: { fontFamily, fontSize: 14, fontWeight: 400 as const },
  bodySmallMedium: { fontFamily, fontSize: 12, fontWeight: 500 as const },
  bodySmallRegular: { fontFamily, fontSize: 12, fontWeight: 400 as const },
};

export const PRIORITY_RANK: Record<string, number> = {
  Essential: 6,
  "Very High": 5,
  High: 4,
  Medium: 3,
  Low: 2,
  "Very Low": 1,
};

export type AskStatus = "looking" | "have" | "covered";

export const ASK_LABELS: Record<AskStatus, string> = {
  looking: "Looking for an intro",
  have: "Have one, want more",
  covered: "Already covered",
};
